import { apiErrorSchema, type ApiError } from '@hearth/shared';
import type { z } from 'zod';

/** A failed API call. `body` is the parsed error envelope when the server sent one. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError['error'] | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** Validation messages for a field, e.g. `fieldErrors('body.email')`. */
  fieldErrors(path: string): string[] {
    return this.body?.fields?.[path] ?? [];
  }
}

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Schema the response must satisfy; also accepted for non-2xx statuses listed in `okStatuses`. */
  schema: z.ZodType<T>;
  okStatuses?: number[];
  signal?: AbortSignal;
  /** Send `body` as plain text instead of JSON (file contents). */
  textBody?: boolean;
  /** Give the schema the raw response text instead of parsed JSON (file contents). */
  textResponse?: boolean;
}

let csrfToken: string | undefined;

/** Called whenever session state is loaded; state-changing requests echo this token. */
export function setCsrfToken(token: string | undefined): void {
  csrfToken = token;
}

/** The CSRF token for requests that can't go through apiRequest (streamed responses). */
export function getCsrfToken(): string | undefined {
  return csrfToken;
}

export async function apiRequest<T>(path: string, opts: RequestOptions<T>): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  if (opts.body !== undefined)
    headers['Content-Type'] = opts.textBody ? 'text/plain; charset=utf-8' : 'application/json';
  if (method !== 'GET' && csrfToken) headers['X-CSRF-Token'] = csrfToken;

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers,
      body:
        opts.body === undefined
          ? undefined
          : opts.textBody
            ? String(opts.body)
            : JSON.stringify(opts.body),
      signal: opts.signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiRequestError(0, null, 'Could not reach the Hearth API. Is panel-api running?');
  }

  const text = await res.text();
  const json: unknown = text ? safeJson(text) : null;

  if (res.ok || opts.okStatuses?.includes(res.status)) {
    const parsed = opts.schema.safeParse(opts.textResponse ? text : json);
    if (!parsed.success) {
      throw new ApiRequestError(res.status, null, `Unexpected response from ${path}.`);
    }
    return parsed.data;
  }

  const envelope = apiErrorSchema.safeParse(json);
  if (envelope.success) {
    throw new ApiRequestError(res.status, envelope.data.error, envelope.data.error.message);
  }
  throw new ApiRequestError(res.status, null, `Request failed with status ${res.status}.`);
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
